// Pure ranking + close-rate helpers extracted from DispatchAssist.jsx so the
// jest test suite can import them without pulling in react-router-dom / the
// full component tree.
//
// Sort order within the qualifying tier group (2026-07-31 revision per Brett):
//   1. close_rate DESC (percentages; from Claire / Big Board PDF snapshot)
//   2. tag match (customer tag ∈ tech.relationship_tags) — tiebreaker
//   3. tier preference (top > mid > developing) — final tiebreaker
// Techs missing close_rate data fall to the BOTTOM of their tier group,
// then the tag/tier tiebreakers apply among themselves.

export function computeMatch({ techs, location, tag, permit, finalTier, closeRates = {} }) {
  const inTerritory = techs.filter((t) => (t.home_territory || []).includes(location));
  const outOfTerritory = techs.filter(
    (t) => !(t.home_territory || []).includes(location) && !!t.cross_territory,
  );
  let candidates = inTerritory.length > 0 ? inTerritory : outOfTerritory;
  const usedCrossTerritory = inTerritory.length === 0 && outOfTerritory.length > 0;

  if (tag && tag !== "none") {
    candidates = candidates.filter((t) => !((t.avoid_tags || []).includes(tag)));
  }

  let permitGated = false;
  if (permit || finalTier === "VeryHigh") {
    const licensed = candidates.filter(
      (t) => t.license === "master" || t.license === "journeyman",
    );
    if (licensed.length > 0) {
      candidates = licensed;
      permitGated = true;
    }
  }

  let ranked;
  if (finalTier === "Low") {
    ranked = candidates.filter((t) => t.tier === "developing");
    if (ranked.length === 0) ranked = candidates.filter((t) => t.tier !== "top");
    if (ranked.length === 0) ranked = candidates;
  } else {
    ranked = candidates.filter((t) => t.tier === "top" || t.tier === "mid");
    if (ranked.length === 0) ranked = candidates;
  }

  const tagRank = (t) => (tag && tag !== "none" && (t.relationship_tags || []).includes(tag) ? 1 : 0);
  const tierRank = (t) => (t.tier === "top" ? 2 : t.tier === "mid" ? 1 : 0);
  const crValue = (t) => {
    const v = closeRates?.[t.name];
    return (typeof v === "number" && !Number.isNaN(v)) ? v : null;
  };
  ranked = [...ranked].sort((a, b) => {
    const ca = crValue(a);
    const cb = crValue(b);
    if (ca == null && cb != null) return 1;
    if (cb == null && ca != null) return -1;
    if (ca != null && cb != null && ca !== cb) return cb - ca;
    const ta = tagRank(a); const tb = tagRank(b);
    if (ta !== tb) return tb - ta;
    return tierRank(b) - tierRank(a);
  });

  return { candidates, ranked, usedCrossTerritory, permitGated };
}


// Auto-scale rule mirrors /api/l10/segue: if every observed close_rate is ≤1.0
// the source posted fractions — multiply by 100 to normalize to percentages.
export function buildCloseRateMap(techPerformanceRows) {
  const rows = (techPerformanceRows || []).filter(
    (r) => r && r.tech_name && r.close_rate != null,
  );
  if (rows.length === 0) return {};
  const asFraction = rows.every((r) => Number(r.close_rate) <= 1.0);
  const scale = asFraction ? 100 : 1;
  const map = {};
  rows.forEach((r) => { map[r.tech_name] = Number(r.close_rate) * scale; });
  return map;
}
